import {
  Card,
  Flex,
  Icon,
  Button,
  Heading,
  FormLabel,
  IconButton,
  FormControl,
  CurrencyInput,
  FormHelperText
} from '@homebotapp/hb-react-component-catalog'
import { useState } from 'react'
import { useIntl, defineMessages } from 'react-intl'

export const MSG = defineMessages({
  otherCosts: {
    id: 'AffordabilityCalculator.otherCosts',
    defaultMessage: 'Other costs'
  },
  propertyTax: {
    id: 'AffordabilityCalculator.propertyTax',
    defaultMessage: 'Property tax (yearly)'
  },
  insurance: {
    id: 'AffordabilityCalculator.insurance',
    defaultMessage: 'Home insurance (monthly)'
  },
  pmi: {
    id: 'AffordabilityCalculator.pmi',
    defaultMessage: 'Mortgage insurance (monthly)'
  },
  pmiHelperText: {
    id: 'AffordabilityCalculator.pmiHelperText',
    defaultMessage: 'Usually required for less than 20% down'
  },
  hoa: {
    id: 'AffordabilityCalculator.hoa',
    defaultMessage: 'HOA fees'
  },
  show: {
    id: 'AffordabilityCalculator.show',
    defaultMessage: 'Show'
  },
  hide: {
    id: 'AffordabilityCalculator.hide',
    defaultMessage: 'Hide'
  }
})

export interface AffordabilityCalculatorOtherCostsSectionProps {
  propertyTax: string
  setPropertyTax: (propertyTax: string) => void
  insurance: string
  setInsurance: (insurance: string) => void
  pmi: string
  setPmi: (pmi: string) => void
  hoa: string
  setHoa: (hoa: string) => void
  setZipCode: (zipCode: string) => void
}

export const AffordabilityCalculatorOtherCostsSection = ({
  propertyTax,
  setPropertyTax,
  insurance,
  setInsurance,
  pmi,
  setPmi,
  hoa,
  setHoa,
  setZipCode
}: AffordabilityCalculatorOtherCostsSectionProps) => {
  const intl = useIntl()
  const [showEditableFields, setShowEditableFields] = useState(false)
  const [isFocused, setIsFocused] = useState(false)

  return (
    <Card p={4}>
      <Button
        w='100%'
        variant='link'
        display='block'
        textAlign='left'
        fontWeight='inherit'
        textColor='currentColor'
        _hover={{ textDecoration: 'none' }}
        onClick={() => setShowEditableFields(!showEditableFields)}
        tracking={{
          guid: 'nmzApd4BxutwRGN3V5b48t',
          ui_context: 'Affordability Calculator Page',
          descriptor: `Other Costs ${showEditableFields ? 'Hide' : 'Show'}`
        }}
      >
        <Flex align='center' justify='space-between'>
          <Heading as='h4' size={['2xs', 'xs', 'sm']}>
            {intl.formatMessage(MSG.otherCosts)}
          </Heading>
          <IconButton
            size='xs'
            variant='link'
            onClick={() => setShowEditableFields(!showEditableFields)}
            aria-label={intl.formatMessage(showEditableFields ? MSG.hide : MSG.show)}
            icon={<Icon height={2} name='play' transform={`rotate(${showEditableFields ? -90 : 90}deg)`} />}
            tracking={{
              guid: 'xvbYQpq7PNYp6aJCPXwb6j',
              ui_context: 'Affordability Calculator Page',
              descriptor: `Other Costs ${showEditableFields ? 'Hide' : 'Show'}`
            }}
          />
        </Flex>
      </Button>
      {showEditableFields && (
        <Flex mt={4} gap={4} direction='column' alignItems='flex-start'>
          <Flex width='100%' direction='column' gap={2} alignItems='flex-start'>
            <Flex gap={4} width='100%' direction={['column', null, null, 'row']}>
              <FormControl>
                <FormLabel size='xs'>{intl.formatMessage(MSG.propertyTax)}</FormLabel>
                <CurrencyInput
                  min={0}
                  size='sm'
                  decimalScale={2}
                  name='propertyTax'
                  value={propertyTax}
                  onFocus={() => setIsFocused(true)}
                  onBlur={() => setIsFocused(false)}
                  onChange={val => {
                    if (isFocused) {
                      setZipCode('')
                      localStorage.setItem('affordabilityCalculatorZipCode', '')
                    }

                    // @ts-ignore
                    setPropertyTax(val)
                    // @ts-ignore
                    localStorage.setItem('affordabilityCalculatorPropertyTax', val)
                  }}
                  tracking={{
                    guid: '9amSF8dsY9JqfnqSo5ieoB',
                    ui_context: 'Affordability Calculator Page',
                    descriptor: 'Property tax input field'
                  }}
                />
              </FormControl>
              <FormControl>
                <FormLabel size='xs'>{intl.formatMessage(MSG.insurance)}</FormLabel>
                <CurrencyInput
                  min={0}
                  size='sm'
                  name='insurance'
                  value={insurance}
                  onFocus={() => setIsFocused(true)}
                  onBlur={() => setIsFocused(false)}
                  decimalScale={2}
                  onChange={val => {
                    if (isFocused) {
                      setZipCode('')
                      localStorage.setItem('affordabilityCalculatorZipCode', '')
                    }

                    // @ts-ignore
                    setInsurance(val)
                    // @ts-ignore
                    localStorage.setItem('affordabilityCalculatorHomeInsurance', val)
                  }}
                  tracking={{
                    guid: '2cntF8H8Gsxd9NYhCTXEDA',
                    ui_context: 'Affordability Calculator Page',
                    descriptor: 'Home Insurance input field'
                  }}
                />
              </FormControl>
            </Flex>
          </Flex>
          <Flex mb={2} gap={4} width='100%' direction={['column', null, null, 'row']}>
            <FormControl>
              <FormLabel size='xs'>{intl.formatMessage(MSG.pmi)}</FormLabel>
              <CurrencyInput
                min={0}
                size='sm'
                decimalScale={2}
                name='pmi'
                value={pmi}
                onChange={val => {
                  // @ts-ignore
                  setPmi(val)
                  // @ts-ignore
                  localStorage.setItem('affordabilityCalculatorPmi', val)
                }}
                tracking={{
                  guid: 'gr211FcU1sVP8aYrcXXY4P',
                  ui_context: 'Affordability Calculator Page',
                  descriptor: 'Mortgage Insurance input field'
                }}
              />
              <FormHelperText fontSize='2xs'>{intl.formatMessage(MSG.pmiHelperText)}</FormHelperText>
            </FormControl>
            <FormControl>
              <FormLabel size='xs'>{intl.formatMessage(MSG.hoa)}</FormLabel>
              <CurrencyInput
                min={0}
                size='sm'
                decimalScale={2}
                name='hoa'
                value={hoa}
                onChange={val => {
                  // @ts-ignore
                  setHoa(val)
                  // @ts-ignore
                  localStorage.setItem('affordabilityCalculatorHoa', val)
                }}
                tracking={{
                  guid: 'nuEjUaW17bAdbCVF7q8M8g',
                  ui_context: 'Affordability Calculator Page',
                  descriptor: 'HOA input field'
                }}
              />
            </FormControl>
          </Flex>
        </Flex>
      )}
    </Card>
  )
}
